import { type FC } from 'react'
import classNames from 'classnames'
import { ADDITIONAL_PROPERTY_FLAG, errorId, getUiOptions, type FieldTemplateProps } from '@rjsf/utils'
import { displayLabelOf, fieldHintOf, fieldNameOf } from './displayLabel'
import { FieldLabel } from './FieldLabel'
import { FormHint } from './FormHint'

const FieldContent: FC<FieldTemplateProps> = ({
  id,
  classNames: baseClassName,
  style,
  label,
  rawDescription,
  rawErrors,
  children,
  hidden,
  required,
  displayLabel,
  uiSchema,
  schema,
}) => {
  const options = getUiOptions(uiSchema)
  const fieldLabel = displayLabelOf(label, schema)
  const hint = displayLabel ? fieldHintOf(id, rawDescription) : undefined
  const showLabel = Boolean(displayLabel && fieldLabel)

  if (hidden) {
    return null
  }

  const errors = rawErrors?.length ? (
    <div id={errorId(id)} className='mt-1'>
      {rawErrors.map((error, index) => (
        <div key={index} className='paragraph-small zocks-text-danger'>
          {error}
        </div>
      ))}
    </div>
  ) : null

  const hiddenLabel = options.labelLayout === 'hidden'

  return (
    <div className={classNames(baseClassName, { 'mb-4': options.compact !== true })} style={style}>
      {hiddenLabel ? (
        <label htmlFor={id} className='visually-hidden'>
          {fieldNameOf(id, label, schema, options.listItem)}
        </label>
      ) : null}
      {hiddenLabel && hint ? (
        <span id={hint.elementId} className='visually-hidden'>
          {hint.text}
        </span>
      ) : null}
      {!hiddenLabel && (showLabel || hint) ? (
        <div className='d-flex flex-column mb-2'>
          {showLabel ? (
            <div className='paragraph-small'>
              <FieldLabel htmlFor={id}>{fieldLabel}</FieldLabel>
              {required ? null : <span className='fw-normal zocks-text-gray-700'> (optional)</span>}
            </div>
          ) : null}
          {hint ? <FormHint id={hint.elementId}>{hint.text}</FormHint> : null}
        </div>
      ) : null}
      {children}
      {errors}
    </div>
  )
}

export const FieldTemplate: FC<FieldTemplateProps> = (props) => {
  const { WrapIfAdditionalTemplate } = props.registry.templates
  if (!(ADDITIONAL_PROPERTY_FLAG in props.schema)) {
    return <FieldContent {...props} />
  }
  return (
    <WrapIfAdditionalTemplate {...props}>
      <FieldContent {...props} />
    </WrapIfAdditionalTemplate>
  )
}
